import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import cn from "classnames";
import { DocumentArrowDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import { api, downloadFile } from "./api";

// Mirrors SUMMARY_SECTIONS in server/src/services/investigations/exports.ts:
// same keys, same page order.
const SECTIONS: { key: string; label: string; hint: string }[] = [
  { key: "overview", label: "Overview", hint: "Headline and summary" },
  { key: "keyPeople", label: "Key people", hint: "Who matters and why" },
  { key: "chronology", label: "Chronology", hint: "Dated sequence of events" },
  { key: "listenFirst", label: "Calls to hear first", hint: "The calls worth a detective's time" },
  { key: "confirmedLeads", label: "Confirmed leads", hint: "Flagged moments you verified" },
  { key: "documents", label: "Case documents", hint: "What each document on file says" },
  { key: "entities", label: "People, numbers, and places", hint: "Names, aliases, and mention counts" },
  { key: "openQuestions", label: "Open questions", hint: "What the analysis could not resolve" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  investigationId: string;
}

// Choose which sections of the brief go in the summary (PDF or Word). Everything is
// on by default; the title block and the AI notice always print.
const ExportSummaryModal: React.FC<Props> = ({ open, onClose, investigationId }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set(SECTIONS.map((s) => s.key)));
  const [exporting, setExporting] = useState(false);
  const [format, setFormat] = useState<"pdf" | "docx">("pdf");
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
    // Every opening starts from "all", so a trimmed export never leaks
    // into the next one.
    if (open) setSelected(new Set(SECTIONS.map((s) => s.key)));
  }, [open]);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const all = selected.size === SECTIONS.length;

  const handleExport = async () => {
    if (!selected.size) return;
    setExporting(true);
    try {
      // In page order, not click order.
      const keys = all ? undefined : SECTIONS.filter((s) => selected.has(s.key)).map((s) => s.key);
      await downloadFile(api.summaryUrl(investigationId, keys, format), `investigation-summary.${format}`);
      toast.success("Summary downloaded");
      onClose();
    } catch (e: any) {
      toast.error(e.message || "Failed to export the summary");
    } finally {
      setExporting(false);
    }
  };

  return (
    <dialog ref={dialogRef} className="modal" onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="modal-box max-w-lg p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <DocumentArrowDownIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Export investigation summary</h3>
            <p className="text-sm text-base-content/60 mt-0.5">The current brief as PDF or Word, ready to hand over. Uncheck anything you'd rather leave out.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="px-6">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-base-content/70">
              Sections
              <span className="ml-1.5 text-base-content/50 font-normal">
                {selected.size} of {SECTIONS.length}
              </span>
            </span>
            <span className="text-xs">
              <button type="button" className="text-blue-600 dark:text-blue-300 hover:underline" onClick={() => setSelected(new Set(SECTIONS.map((s) => s.key)))}>
                Select all
              </button>
              <span className="text-base-content/40 mx-1.5">·</span>
              <button type="button" className="text-blue-600 dark:text-blue-300 hover:underline" onClick={() => setSelected(new Set())}>
                None
              </button>
            </span>
          </div>
          <div className="rounded-lg border border-base-300 divide-y divide-base-300 bg-base-100">
            {SECTIONS.map((s) => {
              const checked = selected.has(s.key);
              return (
                <label key={s.key} className={cn("flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-base-200", checked && "bg-blue-50/60 dark:bg-blue-900/20")}>
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500"
                    checked={checked}
                    onChange={() => toggle(s.key)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-base-content">{s.label}</span>
                    <span className="block text-[11px] text-base-content/50">{s.hint}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11px] text-base-content/50">Sections with nothing to show are skipped automatically. The title block and the AI notice are always included.</p>
        </div>
        <div className="px-6 py-5 flex items-center gap-2">
          <Dropdown size="sm" dense direction="top" menuAlign="start" contentClassName="!z-40" options={[{ value: "pdf", label: "PDF" }, { value: "docx", label: "Word (.docx)" }]} value={format} onChange={(v) => setFormat(v as "pdf" | "docx")} />
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={exporting}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleExport} disabled={exporting || !selected.size}>
              {exporting ? "Generating…" : all ? `Download ${format === "docx" ? "Word" : "PDF"}` : `Download ${selected.size} section${selected.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};

export default ExportSummaryModal;
